import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck, FiCalendar, FiCreditCard, FiGift } from 'react-icons/fi';
import api from '../api';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const planType = searchParams.get('planType');
      const amount = parseInt(searchParams.get('amount'));

      if (!planType || !amount) {
        throw new Error('구독 정보가 올바르지 않습니다.');
      }

      // 서버에서 구독 정보 조회
      const res = await api.get('/api/subscriptions/status');

      if (!res.data.hasSubscription) {
        throw new Error('구독 정보를 찾을 수 없습니다.');
      }

      setSubscription({
        ...res.data.subscription,
        planType,
        amount,
      });
    } catch (error) {
      console.error('Fetch subscription error:', error);
      setError(error.response?.data?.error || error.message || '구독 정보 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <h3>구독 정보를 확인하고 있습니다...</h3>
          <p>잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-page">
        <div className="result-container error">
          <div className="result-icon error">
            <span>✕</span>
          </div>
          <h2>구독 정보 확인 실패</h2>
          <p className="error-message">{error}</p>
          <div className="actions">
            <button className="btn-secondary" onClick={() => navigate('/subscription/plans')}>
              플랜 보기
            </button>
            <button className="btn-primary" onClick={() => navigate('/subscription/manage')}>
              구독 관리
            </button>
          </div>
        </div>
      </div>
    );
  }

  const planName = subscription.planType === 'basic' ? 'Basic' : 'Pro';
  const nextBillingDate = new Date(subscription.nextBillingDate);

  const benefits = {
    basic: [
      '학생 수 제한 해제',
      '매월 10,000P 자동 충전',
      '출결 및 성적 관리',
      '학부모 알림톡 발송',
    ],
    pro: [
      '학생 수 제한 해제',
      '쿼터 무제한 사용',
      '고급 분석 리포트',
      '우선 고객 지원',
    ],
  };

  return (
    <div className="payment-result-page">
      <div className="result-container success">
        {/* 성공 아이콘 */}
        <div className="result-icon success">
          <FiCheck size={64} />
        </div>

        {/* 제목 */}
        <h2>{planName} 플랜 구독 완료!</h2>
        <p className="success-message">
          정기구독이 시작되었습니다
        </p>

        {/* 구독 정보 */}
        <div className="payment-details-card">
          <div className="detail-row">
            <span className="label">
              <FiGift />
              구독 플랜
            </span>
            <span className="value highlight">{planName} 플랜</span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiCreditCard />
              월 결제 금액
            </span>
            <span className="value">{subscription.amount.toLocaleString()}원</span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiCalendar />
              구독 시작일
            </span>
            <span className="value">{new Date(subscription.startDate).toLocaleDateString('ko-KR')}</span>
          </div>

          <div className="detail-row">
            <span className="label">
              <FiCalendar />
              다음 결제일
            </span>
            <span className="value">{nextBillingDate.toLocaleDateString('ko-KR')}</span>
          </div>
        </div>

        {/* 혜택 안내 */}
        <div className="benefits-card">
          <h4>🎉 이제 다음 혜택을 이용하실 수 있습니다</h4>
          <ul className="benefits-list">
            {benefits[subscription.planType]?.map((benefit, index) => (
              <li key={index}>
                <FiCheck className="check-icon" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* 안내 메시지 */}
        <div className="info-box">
          <h4>정기구독 안내</h4>
          <ul>
            <li>매월 같은 날짜에 자동으로 결제됩니다.</li>
            <li>구독은 언제든지 취소할 수 있습니다.</li>
            {subscription.planType === 'basic' && (
              <li>매월 10,000P가 자동으로 충전됩니다.</li>
            )}
            <li>결제 실패 시 알림을 받으실 수 있습니다.</li>
          </ul>
        </div>

        {/* 액션 버튼 */}
        <div className="actions">
          <button className="btn-secondary" onClick={() => navigate('/subscription/manage')}>
            구독 관리
          </button>
          <button className="btn-primary" onClick={() => navigate('/dashboard')}>
            대시보드로 이동
          </button>
        </div>
      </div>

      <style>{`
        .benefits-card {
          background: linear-gradient(135deg, #F5F3FF, #EDE9FE);
          border: 2px solid #C4B5FD;
          border-radius: 12px;
          padding: 24px;
          margin: 24px 0;
          width: 100%;
          max-width: 500px;
          animation: fadeInUp 0.5s ease-out 0.5s both;
        }

        .benefits-card h4 {
          margin: 0 0 16px 0;
          color: #5B21B6;
          font-size: 16px;
        }

        .benefits-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .benefits-list li {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6B21A8;
          font-size: 14px;
        }

        .check-icon {
          color: #10B981;
          flex-shrink: 0;
        }

        .info-box {
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          padding: 20px;
          margin: 24px 0;
          width: 100%;
          max-width: 500px;
          animation: fadeInUp 0.5s ease-out 0.6s both;
        }

        .info-box h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          color: #374151;
        }

        .info-box ul {
          margin: 0;
          padding-left: 20px;
          color: #6B7280;
          font-size: 13px;
          line-height: 1.8;
        }
      `}</style>
    </div>
  );
}
